/**
 * `AutocompleteSourceStore` — fetch + cache + debounced refresh of the editor's autocomplete
 * data (editor-chat.md §2.2, §5 step 7).
 *
 * editor-chat.md §1.2 / §2.2: autocomplete sources are **backend-fed and cached, not computed
 * client-side**. Hashtag completion draws from `list_tags`; wikilink completion draws from
 * `note_titles`. Both are fetched once on editor mount and refreshed on a debounced interval /
 * on save — **never recomputed per keystroke**. The completion extensions filter *locally*
 * against this cached store, so keystroke latency never depends on a round-trip (a hard
 * constraint at D-009's 100K-note scale).
 *
 * This module owns the fetch + the cache + the debounce + the change-notification. The
 * CodeMirror completion extensions (`hashtag-autocomplete.ext`, `wikilink-autocomplete.ext`)
 * read `current()` synchronously at completion time — they never await.
 *
 * The store is framework-agnostic (no React) — it is a plain object with a `subscribe` seam,
 * so it is unit-testable and the React layer (`useAutocompleteSources`) is a thin
 * `useSyncExternalStore` adapter over it.
 */

import { listTags, noteTitles } from '@/api';
import { getLogger } from '@/api';
import type { AutocompleteSources, TagCompletionEntry, TitleCompletionEntry } from '../editorTypes';

const log = getLogger('editor.autocomplete');

/** Debounce window for a refresh — coalesces a burst of `requestRefresh()` calls into one fetch. */
const REFRESH_DEBOUNCE_MS = 750;

/**
 * The page size for the `note_titles` fetch. The backend caps `note_titles` at 1000 rows/page
 * (`NoteTitlesRequest`); at D-009's 100K scale a full title list needs paging — the store
 * fetches pages until exhausted or the safety cap is hit.
 */
const TITLES_PAGE_SIZE = 1000;

/**
 * A safety cap on how many `note_titles` pages the store will fetch in one refresh — bounds
 * the worst case (a pathological corpus) so a refresh can never run unboundedly. 100 pages ×
 * 1000 = 100K titles, which is exactly D-009's stated ceiling.
 */
const TITLES_MAX_PAGES = 100;

/** The page size for the `list_tags` fetch — the tag set is far smaller than the title set. */
const TAGS_PAGE_SIZE = 1000;

/** An empty source set — the store's initial state before the first fetch resolves. */
const EMPTY_SOURCES: AutocompleteSources = {
  tags: [],
  titles: [],
  refreshedAt: new Date(0).toISOString(),
};

/**
 * The autocomplete source store — fetches, caches, debounce-refreshes the editor's tag +
 * title completion data, and notifies subscribers on change.
 *
 * Lifecycle: the editor surface constructs one on mount, calls `requestRefresh()` once
 * immediately, then again (debounced) on each save; calls `dispose()` on unmount. The
 * completion extensions hold a reference and call `current()` synchronously at completion
 * time.
 */
export class AutocompleteSourceStore {
  private sources: AutocompleteSources = EMPTY_SOURCES;
  private readonly listeners = new Set<() => void>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guards against overlapping fetches — a refresh requested while one is in flight is coalesced. */
  private fetching = false;
  /** A refresh requested while a fetch was in flight — run one more fetch when the current ends. */
  private refreshQueued = false;
  private disposed = false;

  /**
   * The current cached sources — read synchronously by the completion extensions at
   * completion time. Never awaits; always returns immediately (possibly the empty/stale set).
   */
  current(): AutocompleteSources {
    return this.sources;
  }

  /**
   * Subscribe to cache changes — the React adapter (`useAutocompleteSources`) wires this into
   * `useSyncExternalStore` so a component re-renders when the sources refresh. Returns an
   * unsubscribe thunk.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Request a refresh — debounced (`REFRESH_DEBOUNCE_MS`). Called once on editor mount and
   * again on each save (editor-chat.md §2.2). A burst of calls coalesces into one fetch; a
   * call while a fetch is in flight queues exactly one follow-up fetch.
   */
  requestRefresh(): void {
    if (this.disposed) return;
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.runRefresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /**
   * Force an immediate (non-debounced) refresh — used by the editor mount path so the first
   * completion after mount has data without waiting out the debounce window. Returns a promise
   * that resolves when the fetch completes (or is coalesced).
   */
  async refreshNow(): Promise<void> {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    await this.runRefresh();
  }

  /** Tear down — clears the debounce timer and drops subscribers. Called on editor unmount. */
  dispose(): void {
    this.disposed = true;
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.listeners.clear();
  }

  // -- internals -------------------------------------------------------------

  /**
   * Run one refresh: fetch the tag set + the (paged) title set, rebuild the cache, notify.
   *
   * A failed fetch is logged and **swallowed** — autocomplete is a convenience, not a
   * correctness surface; a transient backend hiccup must not break typing, and the editor
   * keeps working with the last good (or empty) cache. This is the deliberate, scoped
   * exception editor-chat.md §4 allows for the autocomplete fetch (the editor "must keep
   * working" — §1.1); it is logged at WARN, never silent (conventions.md §3 spirit).
   */
  private async runRefresh(): Promise<void> {
    if (this.disposed) return;
    if (this.fetching) {
      // A fetch is already running — queue exactly one follow-up so the latest request is honored.
      this.refreshQueued = true;
      return;
    }
    this.fetching = true;
    try {
      const [tags, titles] = await Promise.all([this.fetchTags(), this.fetchTitles()]);
      if (this.disposed) return;
      this.sources = {
        tags,
        titles,
        refreshedAt: new Date().toISOString(),
      };
      this.notify();
      log.debug('autocomplete_refreshed', {
        tagCount: tags.length,
        titleCount: titles.length,
      });
    } catch (err) {
      // Swallow-and-log: autocomplete must not break the editor (editor-chat.md §1.1 / §4).
      log.warn('autocomplete_refresh_failed', { error: String(err) });
    } finally {
      this.fetching = false;
      if (this.refreshQueued && !this.disposed) {
        this.refreshQueued = false;
        void this.runRefresh();
      }
    }
  }

  /** Fetch the tag set via `list_tags` and map it to `TagCompletionEntry[]` (editor-chat.md §2.2). */
  private async fetchTags(): Promise<TagCompletionEntry[]> {
    const { data } = await listTags({ limit: TAGS_PAGE_SIZE, minCount: 1 });
    return data.items.map((tag) => ({
      // `Tag.path` is the canonical ltree path; `display` is the human form. The editor inserts
      // `display` (PRD §8.2 — display preserves the casing typed; the backend canonicalizes).
      canonical: tag.path,
      display: tag.display,
      noteCount: tag.noteCount,
      isNested: tag.isNested,
      path: tag.isNested ? tag.path : null,
    }));
  }

  /**
   * Fetch the note-title set via `note_titles`, paging until exhausted or the safety cap is
   * hit (editor-chat.md §2.2 — the title list is paginated at 100K scale).
   */
  private async fetchTitles(): Promise<TitleCompletionEntry[]> {
    const out: TitleCompletionEntry[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < TITLES_MAX_PAGES; page += 1) {
      const { data, meta } = await noteTitles({
        limit: TITLES_PAGE_SIZE,
        ...(cursor !== undefined ? { cursor } : {}),
      });
      for (const item of data.items) {
        out.push({
          noteId: item.noteId,
          title: item.title,
          notebookId: item.notebookId,
        });
      }
      // Stop when the listing is exhausted — `meta.nextCursor` is null at the end.
      if (meta.nextCursor === null || data.items.length === 0) break;
      cursor = meta.nextCursor;
      if (page === TITLES_MAX_PAGES - 1) {
        // Hit the safety cap with more pages available — log it so a pathological corpus is
        // visible, but keep the (large, useful) partial set rather than failing.
        log.warn('autocomplete_titles_capped', { fetched: out.length });
      }
    }
    return out;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}
